import { useEffect, useState, type CSSProperties, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/auth'
import { ApiError } from '../lib/api'

export function LoginPage() {
  const { user, loading, login } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Already signed in -> send to the right home.
  useEffect(() => {
    if (!loading && user) {
      navigate(user.role === 'super_admin' ? '/admin' : '/app/dashboard', { replace: true })
    }
  }, [loading, user, navigate])

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const u = await login(email, password)
      navigate(u.role === 'super_admin' ? '/admin' : '/app/dashboard', { replace: true })
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'invalid_credentials'
          ? 'Incorrect email or password.'
          : 'Something went wrong. Please try again.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{ minHeight: '100%', display: 'grid', placeItems: 'center', padding: 24 }}>
      <form className="card" style={{ width: 360, maxWidth: '100%' }} onSubmit={onSubmit}>
        <img
          src="/logo-lockup.png"
          alt="Resilience — Damage Mitigation & Risk Management"
          style={{ height: 56, width: 'auto', display: 'block', marginBottom: 8 }}
        />
        <p className="page-sub" style={{ marginTop: 4 }}>Damage Management</p>

        <label style={labelStyle}>Email</label>
        <input
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          style={inputStyle}
          required
        />

        <label style={{ ...labelStyle, marginTop: 14 }}>Password</label>
        <input
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          style={inputStyle}
          required
        />

        {error && (
          <p style={{ color: 'var(--red)', fontSize: 13, marginTop: 12 }}>{error}</p>
        )}

        <button
          className="btn btn-primary"
          type="submit"
          disabled={submitting}
          style={{ width: '100%', marginTop: 18, opacity: submitting ? 0.7 : 1 }}
        >
          {submitting ? 'Signing in…' : 'Log In'}
        </button>

        <p style={{ marginTop: 14, fontSize: 13, color: 'var(--gray)', textAlign: 'center' }}>
          New here? <Link to="/register">Create a company account</Link>
        </p>
      </form>
    </div>
  )
}

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  marginBottom: 6,
}
const inputStyle: CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  border: '1px solid var(--line)',
  borderRadius: 'var(--radius-sm)',
  font: 'inherit',
  background: 'var(--bg)',
}
